import type { Control, FieldValues } from 'react-hook-form';
import OdinController, { type OdinControllerProps } from '../../dynamic-form/odin-controller.component';
import ErrorLabel from '../../labels/error-label';
import { PaginatedCombobox, type PaginatedComboboxProps } from './paginated-combobox';
import { PaginatedMultiCombobox, type PaginatedMultiComboboxProps } from './paginated-multi-combobox';
import { StaticCombobox, type StaticComboboxProps } from './static-combobox';
import { StaticMultiCombobox, type StaticMultiComboboxProps } from './static-multi-combobox';

type DistributiveOmit<T, K extends keyof never> = T extends unknown ? Omit<T, K> : never;

type StaticProps<T> = {
  mode: 'static';
  onValueChange?: (value: T | null) => void;
} & DistributiveOmit<StaticComboboxProps<T>, 'value' | 'onValueChange'>;
type StaticMultiProps<T> = {
  mode: 'static-multi';
  onValueChange?: (value: T[]) => void;
} & DistributiveOmit<StaticMultiComboboxProps<T>, 'value' | 'onValueChange'>;
type PaginatedProps<T> = {
  mode: 'paginated';
  onValueChange?: (value: T | null) => void;
} & DistributiveOmit<PaginatedComboboxProps<T>, 'value' | 'onValueChange'>;
type PaginatedMultiProps<T> = {
  mode: 'paginated-multi';
  onValueChange?: (value: T[]) => void;
} & DistributiveOmit<PaginatedMultiComboboxProps<T>, 'value' | 'onValueChange'>;

interface FormProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: string;
  defaultValue?: unknown;
}

export type HfComboboxProps<T, TFieldValues extends FieldValues = FieldValues> = FormProps<TFieldValues> &
  (StaticProps<T> | StaticMultiProps<T> | PaginatedProps<T> | PaginatedMultiProps<T>);

type ControllerRender<T> = OdinControllerProps<T>['render'];

export const HfCombobox = <T, TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  defaultValue,
  ...modeProps
}: HfComboboxProps<T, TFieldValues>) => {
  const render: ControllerRender<T | T[] | null> = ({ field, fieldState: { error } }) => {
    const ariaInvalid = error !== undefined ? true : modeProps['aria-invalid'];

    const body = (() => {
      switch (modeProps.mode) {
        case 'static': {
          const { mode: _, onValueChange: __, ...rest } = modeProps;
          return (
            <StaticCombobox
              {...rest}
              value={(field.value as T | null) ?? null}
              onValueChange={field.onChange}
              aria-invalid={ariaInvalid}
            />
          );
        }
        case 'static-multi': {
          const { mode: _, onValueChange: __, ...rest } = modeProps;
          return (
            <StaticMultiCombobox
              {...rest}
              value={(field.value as T[]) ?? []}
              onValueChange={field.onChange}
              aria-invalid={ariaInvalid}
            />
          );
        }
        case 'paginated': {
          const { mode: _, onValueChange: __, ...rest } = modeProps;
          return (
            <PaginatedCombobox
              {...rest}
              value={(field.value as T | null) ?? null}
              onValueChange={field.onChange}
              aria-invalid={ariaInvalid}
            />
          );
        }
        case 'paginated-multi': {
          const { mode: _, onValueChange: __, ...rest } = modeProps;
          return (
            <PaginatedMultiCombobox
              {...rest}
              value={(field.value as T[]) ?? []}
              onValueChange={field.onChange}
              aria-invalid={ariaInvalid}
            />
          );
        }
        default: {
          modeProps satisfies never;
          throw new Error(`Unhandled HfCombobox mode: ${(modeProps as { mode: string }).mode}`);
        }
      }
    })();

    return (
      <div className="flex flex-col">
        {body}
        {error?.message && <ErrorLabel>{error.message}</ErrorLabel>}
      </div>
    );
  };

  return (
    <OdinController
      name={name}
      control={control}
      defaultValue={defaultValue}
      onChange={modeProps.onValueChange as ((value: T | T[] | null) => void) | undefined}
      render={render}
    />
  );
};
